import React from 'react';
import { Container, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextInput, SelectInput } from '../../../../Helpers/Inputs';
import States from '../../../../Helpers/Lists/States';
import BackNext from '../../../Common/SubComponents/BackNext';
import { customValidators } from '../../../../Helpers/Validators/customValidators';

export default function Payment(props) {
  const formMethods = useForm({
    resolver: yupResolver(Payment.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });

  const { handleSubmit, trigger } = formMethods;

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const payors = [{ value: `${props.insuredInfo.firstName} ${props.insuredInfo.lastName}`, label: `${props.insuredInfo.firstName} ${props.insuredInfo.lastName}` }];

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={5} className='w-100'>
          <div className="formPageTitle">Payment Information</div>
          <FormProvider {...formMethods} >
            <form className="quote-form" onSubmit={handleSubmit(props.onNext)}>
              <div className="mb-3">
                <SelectInput
                  name="payor"
                  id="payor"
                  value={props.formState.payor}
                  onChange={(e) => handleValueChange('payor', e.target.value)}
                  label="Payor"
                  selectOptions={payors}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="address"
                  id="address"
                  label="Address"
                  value={props.formState.address}
                  onChange={(e) => props.onFieldChange('address', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="address2"
                  id="address2"
                  label="Apt or Suite (Optional)"
                  value={props.formState.address2}
                  onChange={(e) => props.onFieldChange('address2', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="city"
                  id="city"
                  label="City"
                  value={props.formState.city}
                  onChange={(e) => props.onFieldChange('city', e.target.value)}
                />
              </div>

              <Row>
                <Col>
                  <div className="mb-3">
                    <SelectInput
                      name="state"
                      id="state"
                      value={props.formState.state}
                      onChange={(e) => handleValueChange('state', e.target.value)}
                      label="State"
                      selectOptions={States()}
                    />
                  </div>
                </Col>

                <Col>
                  <div className="mb-3">
                    <TextInput
                      name="zip"
                      id="zip"
                      label="Zip"
                      value={props.formState.zip}
                      onChange={(e) => props.onFieldChange('zip', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              <div className="mb-3">
                <SelectInput
                  name="paymentMethod"
                  id="paymentMethod"
                  value={props.formState.paymentMethod}
                  onChange={(e) => handleValueChange('paymentMethod', e.target.value)}
                  label="Payment Method"
                  selectOptions={[
                    { value: 'checking', label: 'EFT Draft - Checking Account' },
                    { value: 'savings', label: 'EFT Draft - Savings Account' },
                  ]}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="bankName"
                  id="bankName"
                  label="Financial Institution Name"
                  value={props.formState.bankName}
                  onChange={(e) => props.onFieldChange('bankName', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="bankCity"
                  id="bankCity"
                  label="Financial Institution City"
                  value={props.formState.bankCity}
                  onChange={(e) => props.onFieldChange('bankCity', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <SelectInput
                  name="bankState"
                  id="bankState"
                  value={props.formState.bankState}
                  onChange={(e) => handleValueChange('bankState', e.target.value)}
                  label="Financial Institution State"
                  selectOptions={States()}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="routingNumber"
                  id="routingNumber"
                  label="Routing Number"
                  value={props.formState.routingNumber}
                  onChange={(e) => props.onFieldChange('routingNumber', e.target.value)}
                />
              </div>

              <div className="mb-3">
                <TextInput
                  name="accountNumber"
                  id="accountNumber"
                  label="Account Number"
                  value={props.formState.accountNumber}
                  onChange={(e) => props.onFieldChange('accountNumber', e.target.value)}
                />
              </div>

              <BackNext onBack={props.onBack} />
            </form>
          </FormProvider>
        </Col>
      </Row>
    </Container>
  );
}

Payment.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

Payment.ValidationSchema = object().shape({
  payor: string().required('Payor is required.'),
  address: customValidators.string().isValidNameLimitedSpecialChar('Address is required.', true, true),
  address2: customValidators.string().isValidNameLimitedSpecialChar('', false, true),
  city: customValidators.string().isValidNameLimitedSpecialChar('City is required.', true, false),
  state: string().required('State is required.'),
  zip: string()
    .required('Zip code is required.')
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
  paymentMethod: string().required('Payment Method is required.'),
  bankName: customValidators.string().isValidNameLimitedSpecialChar('Financial Institution Name is required.', true, false),
  bankCity: customValidators.string().isValidNameLimitedSpecialChar('Financial Institution City is required.', true, false),
  bankState: string().required('Financial Institution State is required.'),
  routingNumber: string()
    .required('Routing Number is required.')
    .matches(/^[0-9]{9}$/, 'Routing number must be 9 number characters.'),
  accountNumber: string()
    .required('Account Number is required.')
    .matches(/^[0-9]{1,17}$/, 'Account number must be numbers with a max of 17 characters.'),
});

Payment.DefaultFormState = (prefill = false) => {
  return {
    payor: prefill ? 'Reymundo Puga' : '',
    address: prefill ? '123 Main Street' : '',
    address2: prefill ? '#123' : '',
    city: prefill ? 'Atlanta' : '',
    state: prefill ? 'ID' : '',
    zip: prefill ? '83716' : '',
    paymentMethod: prefill ? 'checking' : '',
    bankName: prefill ? '1st Bank of Nowhere' : '',
    bankCity: prefill ? 'Atlanta' : '',
    bankState: prefill ? 'ID' : '',
    routingNumber: prefill ? '123456789' : '',
    accountNumber: prefill ? '11111111112' : '',
  };
};
